/**
 * ShellBreadcrumbs — breadcrumb navigation derived from the canonical route registry.
 *
 * Uses each route's `breadcrumbKeys` to render translated labels. Hub root
 * pages (single-segment paths) are skipped to avoid redundant breadcrumbs
 * when the top tabs already indicate location.
 */
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs, Typography, Link as MuiLink, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/styles/use-finance-theme';
import { ROUTE_IDS } from '@finance/contracts';
import { getRouteByPath, getRouteById } from '../../routes/registry';
import { resolveTKey } from '../../i18n/resolve-key';
import type { TranslationKey } from '@finance/contracts';

interface ShellBreadcrumbsProps {
  pathname: string;
}

const HUB_ROOT_IDS = [
  ROUTE_IDS.DASHBOARD,
  ROUTE_IDS.DATA_HUB,
  ROUTE_IDS.REPORTS,
  ROUTE_IDS.ADMIN_HUB,
  ROUTE_IDS.SETTINGS,
];

const HUB_ROOT_PATHS = new Set(
  HUB_ROOT_IDS.map((id) => getRouteById(id)?.path).filter((p): p is string => !!p),
);

interface Crumb {
  label: string;
  to?: string;
}

export function ShellBreadcrumbs({ pathname }: ShellBreadcrumbsProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const crumbs = useMemo<Crumb[]>(() => {
    const route = getRouteByPath(pathname);
    if (!route || route.breadcrumbKeys.length <= 1) return [];
    if (HUB_ROOT_PATHS.has(pathname)) return [];

    const segments = pathname.split('/').filter(Boolean);

    return route.breadcrumbKeys.map((key: TranslationKey, index, arr) => {
      const isLast = index === arr.length - 1;
      const label = t(resolveTKey(key));
      if (isLast) return { label };
      const parentPath = '/' + segments.slice(0, index + 1).join('/');
      return { label, to: parentPath };
    });
  }, [pathname, t]);

  if (crumbs.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }} data-testid="shell-breadcrumbs">
      <Breadcrumbs
        aria-label={t('shell:breadcrumbs.ariaLabel')}
        separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled', mx: 0.25 }} />}
        sx={{
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.8125rem',
          },
        }}
      >
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;
          if (crumb.to && !isLast) {
            return (
              <MuiLink
                key={`${crumb.label}-${index}`}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="text.secondary"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minWidth: 24,
                  minHeight: 24,
                  maxWidth: theme.financeLayout.page.breadcrumbMaxWidth,
                  '@media (pointer: coarse), (max-width: 600px)': {
                    minWidth: 44,
                    minHeight: 44,
                  },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: '0.8125rem',
                }}
              >
                {crumb.label}
              </MuiLink>
            );
          }
          return (
            <Typography
              key={`${crumb.label}-${index}`}
              color="text.primary"
              aria-current={isLast ? 'page' : undefined}
              sx={{
                maxWidth: theme.financeLayout.page.breadcrumbMaxWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.8125rem',
                fontWeight: 500,
              }}
            >
              {crumb.label}
            </Typography>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
}
